import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadExcel, downloadPDF, printThermal } from "../utils/exportHelpers";
import { formatStoredMoney } from "../utils/currency";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportControlBar from "../components/ReportControlBar";
import { API_ENDPOINTS } from "../config/constants";

const defaultData = (month) => ({
  month,
  restaurant: "",
  total_items: 0,
  total_quantity: 0,
  total_amount: 0,
  total_orders: 0,
  items: [],
});

const MonthWiseItemReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [month, setMonth] = useState(currentMonthKey());
  const [data, setData] = useState(defaultData(currentMonthKey()));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}").data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, month]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await fetch(
        API_ENDPOINTS.REPORT_MONTH_WISE_ITEMS,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId, month }),
        }
      );
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || "Failed to load report");
      setData({
        ...defaultData(month),
        ...json,
        items: Array.isArray(json?.items) ? json.items : [],
      });
      setPage(0);
    } catch (e) {
      toast.error(e.message || "Unable to load report");
      setData(defaultData(month));
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const fmtINR = (n) => formatStoredMoney(n, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return { start, end, rows: data.items.slice(start, end) };
  }, [data.items, page]);

  const pageCount = Math.ceil((data.items.length || 0) / rowsPerPage);

  const exportCsv = () => {
    const rows = data.items.map((it, idx) => [
      idx + 1,
      it.item_name,
      it.category_name || "Uncategorized",
      it.total_quantity,
      it.total_orders,
      it.total_amount,
    ]);
    downloadCSV(
      `month_wise_items_${month}.csv`,
      ["#", "Item", "Category", "Qty", "Orders", "Amount"],
      rows
    );
  };

  const exportExcel = () => {
    const rows = data.items.map((it, idx) => [
      idx + 1,
      it.item_name,
      it.category_name || "Uncategorized",
      it.total_quantity,
      it.total_orders,
      it.total_amount,
    ]);
    downloadExcel(
      `month_wise_items_${month}.xlsx`,
      ["#", "Item", "Category", "Qty", "Orders", "Amount"],
      rows
    );
  };

  const exportPdf = () => downloadPDF(reportRef.current, `month_wise_items_${month}.pdf`);

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />

      <ReportPageHeader
        title="Month-wise Item Report"
        subtitle="Items sold during the selected month."
        stats={[
          { label: "Total Items", value: data.total_items || 0 },
          { label: "Total Qty", value: data.total_quantity || 0 },
          { label: "Total Orders", value: data.total_orders || 0 },
          { label: "Total Amount", value: fmtINR(data.total_amount) },
        ]}
      />

      <ReportControlBar>
        <ReportPeriodPicker month={month} onMonthChange={(m) => setMonth(m)} />
        <PrimaryBtn onClick={() => fetchReport({ refresh: true })}>
          <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
        </PrimaryBtn>
      </ReportControlBar>

      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading…</Center>
        ) : data.items.length === 0 ? (
          <Center>No data for this month.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item</th>
                  <th>Category</th>
                  <th>Qty</th>
                  <th>Orders</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {paginated.rows.map((it, idx) => (
                  <tr key={it.product_id || idx}>
                    <td>{paginated.start + idx + 1}</td>
                    <td>{it.item_name}</td>
                    <td>{it.category_name || "Uncategorized"}</td>
                    <td>{it.total_quantity}</td>
                    <td>{it.total_orders}</td>
                    <td>{fmtINR(it.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}

        <Pager>
          <PageBtn disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            ‹ Prev
          </PageBtn>
          <PageInfo>
            Page <strong>{pageCount ? page + 1 : 0}</strong> of <strong>{pageCount}</strong>
          </PageInfo>
          <PageBtn disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>
            Next ›
          </PageBtn>
        </Pager>

        <ActionsRow style={{ marginTop: 14 }}>
          <PrimaryBtn onClick={exportExcel}>Excel</PrimaryBtn>
          <PrimaryBtn onClick={exportPdf}>PDF</PrimaryBtn>
          <PrimaryBtn onClick={() => printThermal(reportRef.current, `month_wise_items_${month}`)}>
            Thermal Print
          </PrimaryBtn>
          <PrimaryBtn onClick={exportCsv}>CSV</PrimaryBtn>
        </ActionsRow>
      </Card>
    </Page>
  );
};

export default MonthWiseItemReportPage;

const currentMonthKey = () => toMonthKey(new Date());
const toMonthKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const Page = styled.div`
  background: radial-gradient(circle at 20% 20%, #f5f8ff, #eef2ff 55%, #f5f7fb 100%);
  min-height: 100vh;
  padding: 18px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  background: linear-gradient(135deg, #075f9e, #0786c5);
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 8px 14px;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(7, 95, 158, 0.15);
  transition: all 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(7, 95, 158, 0.25);
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
`;

const Center = styled.div`
  text-align: center;
  color: #64748b;
  padding: 24px 0;
  font-weight: 600;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    font-size: 14px;
  }
  th {
    background: #f8fafc;
    color: #334155;
    font-weight: 800;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.5px;
  }
  tr:hover td {
    background: #f8fafc;
  }
`;

const Pager = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  flex-wrap: wrap;
`;

const PageBtn = styled.button`
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 7px 14px;
  font-weight: 700;
  color: #075f9e;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  color: #64748b;
  font-size: 0.88rem;
`;
